/**
 * Shared chat state and persistence for the unified AI chat
 * (Script Generation + Chat AI in the script editor).
 * Uses localStorage so chats persist across browser sessions.
 */

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  intent?: string;
  /** From AI Script Generation: reasoning, code, or correction. risk_analysis = Risk & Signals widget. */
  type?: 'reasoning' | 'code' | 'correction' | 'risk_analysis';
  /** From reasoning messages: next exploration ideas (buttons). */
  nextExplorations?: string[];
  /** Risk & Signals analysis payload; when set, type should be 'risk_analysis' and content is a short label. */
  riskAnalysis?: unknown;
  /** Evaluation mode and bars when this message is risk_analysis (for display and AI context). */
  riskAnalysisContext?: { evaluationMode: string; simulationBars?: number };
}

const CHAT_STORAGE_KEY = 'quantquack-ai-chat';

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Omit distribution arrays from risk analysis so persisted payload fits and is serializable. */
function omitDistributions<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(omitDistributions) as T;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key === 'distribution') continue;
    (out as Record<string, unknown>)[key] = omitDistributions(value);
  }
  return out as T;
}

/** Build a safe payload for one message so one bad message never breaks the whole save. */
function messageToSerializable(m: ChatMessage): SerializedMessage {
  let riskPayload: unknown = m.riskAnalysis;
  if (m.type === 'risk_analysis' && m.riskAnalysis != null) {
    try {
      riskPayload = omitDistributions(m.riskAnalysis);
      JSON.stringify(riskPayload);
    } catch {
      const meta = (m.riskAnalysis as { metadata?: unknown })?.metadata;
      riskPayload = { _truncated: true, metadata: meta ?? null };
    }
  }
  return {
    role: m.role,
    content: m.content,
    timestamp: m.timestamp.toISOString(),
    intent: m.intent,
    type: m.type,
    nextExplorations: m.nextExplorations,
    riskAnalysis: riskPayload,
    riskAnalysisContext: m.riskAnalysisContext,
  };
}

export function getChatStorageKey(scriptId: string | null | undefined): string {
  return `${CHAT_STORAGE_KEY}-${scriptId ?? 'new'}`;
}

/** One-time migration: copy from sessionStorage to localStorage if present and localStorage is empty. */
function migrateSessionToLocal(scriptId: string | null | undefined): void {
  const key = getChatStorageKey(scriptId);
  try {
    if (typeof window === 'undefined') return;
    const rawSession = window.sessionStorage?.getItem(key);
    if (!rawSession) return;
    const local = window.localStorage?.getItem(key);
    if (local) return;
    window.localStorage?.setItem(key, rawSession);
  } catch {
    // ignore
  }
}

type SerializedMessage = {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  intent?: string;
  type?: 'reasoning' | 'code' | 'correction' | 'risk_analysis';
  nextExplorations?: string[];
  riskAnalysis?: unknown;
  riskAnalysisContext?: { evaluationMode: string; simulationBars?: number };
};

export function loadChatFromSession(scriptId: string | null | undefined): ChatMessage[] | null {
  try {
    migrateSessionToLocal(scriptId);
    const storage = getStorage();
    if (!storage) return null;
    const raw = storage.getItem(getChatStorageKey(scriptId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SerializedMessage[];
    if (!Array.isArray(parsed)) return null;
    return parsed.map((m) => ({
      role: m.role,
      content: m.content,
      timestamp: new Date(m.timestamp),
      intent: m.intent,
      type: m.type,
      nextExplorations: m.nextExplorations,
      riskAnalysis: m.riskAnalysis,
      riskAnalysisContext: m.riskAnalysisContext,
    }));
  } catch {
    return null;
  }
}

export function saveChatToSession(scriptId: string | null | undefined, messages: ChatMessage[]): void {
  const storage = getStorage();
  if (!storage) return;
  const key = getChatStorageKey(scriptId);
  try {
    if (messages.length === 0) {
      storage.removeItem(key);
      return;
    }
    const toSerialize = messages.map(messageToSerializable);
    const serialized = JSON.stringify(toSerialize);
    storage.setItem(key, serialized);
  } catch (e) {
    if (import.meta.env?.DEV) {
      console.warn('[chat-storage] saveChatToSession failed:', e);
    }
  }
}

export function parseChatFromJson(raw: string): ChatMessage[] | null {
  try {
    const parsed = JSON.parse(raw) as SerializedMessage[];
    if (!Array.isArray(parsed)) return null;
    return parsed.map((m) => ({
      role: m.role,
      content: m.content,
      timestamp: new Date(m.timestamp),
      intent: m.intent,
      type: m.type,
      nextExplorations: m.nextExplorations,
      riskAnalysis: m.riskAnalysis,
      riskAnalysisContext: m.riskAnalysisContext,
    }));
  } catch {
    return null;
  }
}
